<template lang="html">
  <div class="flex min-h-full flex-1 flex-col">
    <CatalogRootCards v-if="pageType === 'root'" />

    <CatalogQueryView
      v-else-if="pageType === 'query'"
      :query="searchQuery"
    />

    <CategoryHub
      v-else-if="pageType === 'hub' && selectedCategory"
      :node="selectedCategory"
      :by-ids="all ?? {}"
    />

    <CatalogPlpView
      v-else-if="pageType === 'plp'"
      :selected-category="selectedCategory"
    />
  </div>
</template>

<script setup lang="ts">
import CatalogPlpView from '#Products/components/Catalog/CatalogPlpView.vue'
import CatalogQueryView from '#Products/components/Catalog/CatalogQueryView.vue'
import CatalogRootCards from '#Products/components/Catalog/CatalogRootCards.vue'
import CategoryHub from '#Products/components/Catalog/Hub/CategoryHub.vue'
import type { CategoryDto } from '#Products/models/Pim/CategoryDto'
import { resolveCategoryDestination } from '#Products/support/categoryDestination'

definePageMeta({
  name: 'ProductsCatalog',
  layoutContainer: 'full',
})

const route = useRoute()
const { routing } = useProducts()
const { all } = await useCategories()

const slug = computed(() => route.params.category as string | undefined)
const searchQuery = computed(() => String(route.query.q ?? ''))

const selectedCategory = computed<CategoryDto | undefined>(() => {
  if (!slug.value) return undefined
  const id = routing.extractIdFromSlug(slug.value)
  return all.value?.[id] as CategoryDto | undefined
})

// ЧПУ категории — на первом рендере (SSR): 404 для несуществующей категории,
// 301 с «неправильного» слага на канонический (дубль-контент)
if (slug.value) {
  if (!selectedCategory.value) {
    throw createError({ statusCode: 404, statusMessage: 'Category not found' })
  }
  const canonicalSlug = routing.getCategorySlug(selectedCategory.value)
  if (slug.value !== canonicalSlug) {
    await navigateTo(routing.getCatalog(selectedCategory.value).route, {
      redirectCode: 301,
      replace: true,
    })
  }
}

type CatalogPageType = 'root' | 'query' | 'hub' | 'plp'

const pageType = computed<CatalogPageType>(() => {
  if (!slug.value) {
    return 'q' in route.query ? 'query' : 'root'
  }

  if (!selectedCategory.value) {
    return 'plp'
  }

  return resolveCategoryDestination(selectedCategory.value)
})

// Always register (same page remounts across root/query/hub/plp on client nav).
// Query view overrides title via its own useSeoMeta while mounted.
useCategorySeo(selectedCategory, all)
</script>
